export type ConsoleLogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
export type CapturedLogLevel = ConsoleLogLevel | 'onerror' | 'unhandledrejection' | 'network';

export type CapturedLogEntry = {
	level: CapturedLogLevel;
	message: string;
	timestamp: string;
	stack?: string;
};

const MAX_ENTRIES = 2000;
const MAX_ENTRY_LENGTH = 4000;
const CONSOLE_LEVELS: Array<ConsoleLogLevel> = ['log', 'info', 'warn', 'error', 'debug'];

const buffer: Array<CapturedLogEntry> = [];
let activeCleanup: (() => void) | null = null;

// eslint-disable-next-line @typescript-eslint/ban-types -- WeakSet requires the built-in `object` bound
const jsonReplacer = (seen: WeakSet<object>) => (_key: string, val: unknown): unknown => {
	if (typeof val === 'bigint') return val.toString();
	if (val instanceof Node) return `[DOMNode ${val.nodeName}]`;
	if (typeof val !== 'object' || val === null) return val;
	if (seen.has(val)) return '[Circular]';
	seen.add(val);
	return val;
};

// class instances (e.g. domain objects) can hold cyclic refMap back-references, so a plain JSON.stringify will throw
const safeStringify = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (value instanceof Error) return value.stack ?? `${value.name}: ${value.message}`;
	try {
		// eslint-disable-next-line @typescript-eslint/ban-types -- WeakSet requires the built-in `object` bound
		const json = JSON.stringify(value, jsonReplacer(new WeakSet<object>()));
		return json ?? String(value);
	} catch {
		return String(value);
	}
};

const truncateMessage = (message: string): string => (
	message.length > MAX_ENTRY_LENGTH ? `${message.slice(0, MAX_ENTRY_LENGTH)}...[truncated]` : message
);

const pushEntry = (entry: CapturedLogEntry): void => {
	buffer.push({ ...entry, message: truncateMessage(entry.message) });
	if (buffer.length > MAX_ENTRIES) buffer.shift();
};

/**
 * Intercepts console.log/info/warn/error/debug plus window error and unhandledrejection events into a bounded
 * in-memory ring buffer, so a diagnostics download can include the console history leading up to a crash.
 * Always calls through to the original console method first -- capture never changes visible console behaviour.
 */
export const startConsoleCapture = (): (() => void) => {
	if (activeCleanup) return activeCleanup;

	const originals: Record<ConsoleLogLevel, (...args: Array<unknown>) => void> = {
		log: console.log,
		info: console.info,
		warn: console.warn,
		error: console.error,
		debug: console.debug
	};

	CONSOLE_LEVELS.forEach((level) => {
		console[level] = (...args: Array<unknown>) => {
			originals[level](...args);
			pushEntry({ level, message: args.map(safeStringify).join(' '), timestamp: new Date().toISOString() });
		};
	});

	const handleError = (event: ErrorEvent): void => {
		const { message, error } = event;
		pushEntry({ level: 'onerror', message, timestamp: new Date().toISOString(), stack: error?.stack });
	};

	const handleRejection = (event: PromiseRejectionEvent): void => {
		const { reason } = event;
		const stack = reason instanceof Error ? reason.stack : null;
		pushEntry({
			level: 'unhandledrejection',
			message: reason instanceof Error ? reason.message : safeStringify(reason),
			timestamp: new Date().toISOString(),
			...(stack ? { stack } : {})
		});
	};

	window.addEventListener('error', handleError);
	window.addEventListener('unhandledrejection', handleRejection);

	// Browsers print failed requests to DevTools directly, NOT through console.*, so the
	// interceptors below record failed fetch/XHR responses (status + body) into the same buffer.
	const originalFetch = window.fetch;
	window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
		const url = input instanceof Request ? input.url : String(input);
		try {
			const response = await originalFetch(input, init);
			if (!response.ok) {
				let body = '';
				try { body = await response.clone().text(); } catch { /* body unreadable */ }
				pushEntry({ level: 'network', message: `HTTP ${response.status} ${method} ${url} ${body}`, timestamp: new Date().toISOString() });
			}
			return response;
		} catch (err) {
			pushEntry({ level: 'network', message: `NETWORK ERROR ${method} ${url} — ${err instanceof Error ? err.message : safeStringify(err)}`, timestamp: new Date().toISOString() });
			throw err;
		}
	};

	const xhrProto = XMLHttpRequest.prototype;
	const originalXhrOpen = xhrProto.open;
	const originalXhrSend = xhrProto.send;
	const xhrMeta = new WeakMap<XMLHttpRequest, { method: string; url: string }>();

	xhrProto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: Array<unknown>): void {
		xhrMeta.set(this, { method: method.toUpperCase(), url: String(url) });
		(originalXhrOpen as (...a: Array<unknown>) => void).apply(this, [method, url, ...rest]);
	} as unknown as typeof xhrProto.open;

	xhrProto.send = function (this: XMLHttpRequest, ...args: Array<unknown>): void {
		this.addEventListener('loadend', () => {
			if (this.status !== 0 && this.status < 400) return;
			const meta = xhrMeta.get(this);
			let body = '';
			try { body = this.responseType === '' || this.responseType === 'text' ? this.responseText : ''; } catch { /* body unreadable */ }
			pushEntry({ level: 'network', message: `HTTP ${this.status} ${meta?.method ?? ''} ${meta?.url ?? ''} ${body}`, timestamp: new Date().toISOString() });
		});
		(originalXhrSend as (...a: Array<unknown>) => void).apply(this, args);
	} as unknown as typeof xhrProto.send;

	activeCleanup = () => {
		CONSOLE_LEVELS.forEach((level) => { console[level] = originals[level]; });
		window.removeEventListener('error', handleError);
		window.removeEventListener('unhandledrejection', handleRejection);
		window.fetch = originalFetch;
		xhrProto.open = originalXhrOpen;
		xhrProto.send = originalXhrSend;
		activeCleanup = null;
	};
	return activeCleanup;
};

export const getCapturedLogs = (): Array<CapturedLogEntry> => [...buffer];
